import Decimal from 'decimal.js';

const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

const eurFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/** Prototype `money(n)`: `1.234,50 €`. */
export function money(n: number | string | null | undefined): string {
  return eurFormatter.format(Number(n || 0));
}

/**
 * Prototype `num(n)`: sq-AL number with max 2 fraction digits — implemented without Intl because browsers
 * without Albanian ICU data (e.g. Chrome on Windows) fall back to English formatting. Matches
 * Intl.NumberFormat('sq-AL'): decimal comma, groups of three separated by U+00A0 once the integer part has
 * at least five digits (minimumGroupingDigits = 2), half-up rounding to two decimals.
 */
export function num(n: number | string | null | undefined): string {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return '0';
  const rounded = new D(v).toDecimalPlaces(2);
  const neg = rounded.isNegative();
  const [intPart = '0', frac = ''] = rounded.abs().toFixed().split('.');
  const grouped = intPart.length >= 5 ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') : intPart;
  return (neg ? '-' : '') + grouped + (frac ? ',' + frac : '');
}

/** Prototype `roundInvoiceMoney(n)`: round to 2 decimals (half up, decimal arithmetic). */
export function roundInvoiceMoney(n: number | string | null | undefined): number {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return 0;
  return new D(v).toDecimalPlaces(2).toNumber();
}

/** Round to 3 decimals for quantities. */
export function roundQty(n: number | string | null | undefined): number {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return 0;
  return new D(v).toDecimalPlaces(3).toNumber();
}

export function toCents(amount: number): number {
  return new D(amount).times(100).toDecimalPlaces(0).toNumber();
}

export function fromCents(cents: number): number {
  return new D(cents).div(100).toNumber();
}

/** qty × unit price rounded to 2 decimals, computed in decimal arithmetic. */
export function lineTotal(qty: number, price: number): number {
  return new D(qty || 0)
    .times(price || 0)
    .toDecimalPlaces(2)
    .toNumber();
}

/** Sum of money values in decimal arithmetic, rounded to 2 decimals. */
export function sumMoney(values: Iterable<number>): number {
  let acc = new D(0);
  for (const v of values) acc = acc.plus(v || 0);
  return acc.toDecimalPlaces(2).toNumber();
}

/** Percentage of a money amount (e.g. VAT), rounded to 2 decimals. */
export function percentOf(amount: number, percent: number): number {
  return new D(amount || 0)
    .times(percent || 0)
    .div(100)
    .toDecimalPlaces(2)
    .toNumber();
}

/**
 * Single decimal parser for quantities and prices (task.md §6.1): accepts `,` or `.` as the
 * decimal separator, rejects empty, NaN, negative and malformed input. Returns null on rejection.
 */
export function parseDecimal(raw: string | number | null | undefined): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw >= 0 ? raw : null;
  const s = String(raw ?? '')
    .trim()
    .replace(/\s+/g, '');
  if (!s) return null;
  const normalized = s.replace(',', '.');
  if (!/^(?:\d+(?:\.\d+)?|\.\d+|\d+\.)$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Prototype `invoiceMoneyNumber(raw)`: tolerant parser for OCR/invoice numbers with mixed
 * thousand/decimal separators. Ported 1:1 (may return negatives; used only for invoice text).
 */
export function invoiceMoneyNumber(raw: string | number | null | undefined): number | null {
  let s = String(raw ?? '')
    .trim()
    .replace(/[€$£\s]/g, '')
    .replace(/[^0-9,.-]/g, '');
  if (!s) return null;
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  const c = s.lastIndexOf(',');
  const d = s.lastIndexOf('.');
  if (c >= 0 && d >= 0) {
    if (c > d) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (c >= 0) {
    const f = s.length - c - 1;
    s = f === 2 ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (d >= 0) {
    const parts = s.split('.');
    if (parts.length > 2) {
      const dec = parts.pop() ?? '';
      s = parts.join('') + '.' + dec;
    } else if (parts.length === 2 && (parts[1] ?? '').length === 3 && (parts[0] ?? '').length <= 3) {
      s = (parts[0] ?? '') + (parts[1] ?? '');
    }
  }
  const n = Number(s);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}
